import { useLocation } from '@solidjs/router';
import { type JSX, Match, Switch } from 'solid-js';
import { t } from '../i18n';
import { isForbidden } from '../lib/api';
import { retryEmail, useEmailStatus } from '../lib/email-status';
import { createMutation } from '../lib/mutation';
import { orgFromPath, orgHref } from '../lib/org';
import { DeniedState } from './DeniedState';
import { EmailBounceNotice } from './EmailBounceNotice';
import { ErrorState } from './ErrorState';

// Container for the hard-bounce notice (plan 101 N1): reads the caller's email
// status and asks for a retry on request. Mounted on the Notifications settings
// page, where "how we reach you" lives.
//
// Not a `<QueryBoundary/>`: that boundary paints a skeleton while loading and an
// empty-state line when there is nothing, and a banner that exists only when
// something is wrong must paint NOTHING in both cases. Its two error arms stay,
// in its order (403 first, so a denial never offers a Retry that cannot help) —
// a failed read must not look like "your email is fine".
//
// A retry re-READS the status (`onSettled`), never hides the notice from the
// echo: the server decides whether the address is still suppressed.

export function EmailBounceAlert(props: {
  token: string | null;
  onNotice?: (msg: string) => void;
}): JSX.Element {
  const query = useEmailStatus(props.token);
  const location = useLocation();

  const retry = createMutation({
    call: () => retryEmail(props.token),
    fallback: t('email_bounce.resume_error'),
    onSuccess: (result) => {
      const status = query.data;
      if (result.cleared && status?.bouncing)
        props.onNotice?.(t('email_bounce.resumed_notice', { address: status.address }));
    },
    onError: (message) => props.onNotice?.(message),
    onSettled: () => query.refetch(),
  });

  return (
    <Switch>
      <Match when={query.isError && isForbidden(query.error)}>
        <DeniedState error={query.error} />
      </Match>
      <Match when={query.isError}>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Match>
      <Match when={query.data?.bouncing === true && query.data}>
        {(status) => (
          <EmailBounceNotice
            address={status().address}
            since={status().since}
            emailRoutesHref={orgHref('/account/email-routes', orgFromPath(location.pathname))}
            resuming={retry.pending()}
            onResume={() => void retry.run()}
          />
        )}
      </Match>
    </Switch>
  );
}
